// src/examples/Navbars/AdminTopNavBar.js
import React, { useState, useMemo, useEffect } from "react";
import api from "lib/axios";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemIcon,
  Divider,
  Box,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BarChartIcon from "@mui/icons-material/BarChart";
import PeopleIcon from "@mui/icons-material/People";
import TableChartIcon from "@mui/icons-material/TableChart";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import PropTypes from "prop-types";
import { BRAND } from "assets/theme/brand";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const AdminTopNavBar = ({ forcedRole }) => {
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const role = String(forcedRole || auth?.role || localStorage.getItem("role") || "")
    .toLowerCase()
    .trim();
  if (role && role !== "admin") return null;

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeGroupTop, setActiveGroupTop] = useState(0);
  const [pendingSkillsCount, setPendingSkillsCount] = useState(0);
  const hasPending = pendingSkillsCount > 0;

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      await api.post("/logout");
    } catch {}
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("role");
    } catch {}
    window.location.replace("/portal/authentication/sign-in");
  };

  useEffect(() => {
    let mounted = true;
    const fetchPending = async () => {
      try {
        const { data } = await api.get("/skills", {
          params: { status: "pending" },
          withCredentials: true,
        });
        if (mounted) setPendingSkillsCount(Array.isArray(data) ? data.length : 0);
      } catch {}
    };
    fetchPending();
    const id = setInterval(fetchPending, 60_000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const menuGroups = [
    {
      label: "Stats",
      icon: <BarChartIcon />,
      items: [{ label: "Stats", path: "/admin-dashboard", icon: <BarChartIcon /> }],
    },
    {
      label: "Users",
      icon: <PeopleIcon />,
      items: [
        { label: "Dummy Users", path: "/contractors/test", icon: <PeopleIcon /> },
        { label: "Employees", path: "/employees", icon: <PeopleIcon /> },
        { label: "Tradespeople", path: "/contractors/live", icon: <PeopleIcon /> },
      ],
    },
    {
      label: "Sales",
      icon: <MonetizationOnIcon />,
      items: [
        {
          label: "Billing",
          url: "https://dashboard.stripe.com/",
          external: true,
          icon: <MonetizationOnIcon />,
        },
        { label: "UTM Marketing", path: "/marketing-utm", icon: <MonetizationOnIcon /> },
        { label: "Sales Overview", path: "/sales-overview", icon: <MonetizationOnIcon /> },
      ],
    },
    {
      label: "Profile",
      icon: <PersonIcon />,
      items: [
        { label: "Profile", path: "/profile?tab=app", icon: <PersonIcon /> },
        { label: "Details", path: "/profile?tab=details", icon: <PersonIcon /> },
        { label: "Projects", path: "/profile?tab=projects", icon: <PersonIcon /> },
        { label: "Settings", path: "/profile?tab=settings", icon: <SettingsIcon /> },
      ],
    },
    {
      label: "Admin",
      icon: <AdminPanelSettingsIcon />,
      items: [
        { label: "Logs", path: "/logs", icon: <AdminPanelSettingsIcon /> },
        { label: "Notifications", path: "/notifications", icon: <AdminPanelSettingsIcon /> },
        {
          label: "Insurance Review",
          path: "/admin-insurance-review",
          icon: <AdminPanelSettingsIcon />,
        },
        { label: "DBS Review", path: "/admin-dbs-review", icon: <AdminPanelSettingsIcon /> },
        { label: "Lead Review", path: "/admin-lead-review", icon: <AdminPanelSettingsIcon /> },
        {
          label: "Reset Password",
          path: "/authentication/reset-password",
          icon: <AdminPanelSettingsIcon />,
        },
      ],
    },
    {
      label: "Data Tables",
      icon: <TableChartIcon />,
      items: [
        { label: "Services Offered", path: "/skills", icon: <TableChartIcon /> },
        { label: "Trade Types", path: "/professions", icon: <TableChartIcon /> },
        { label: "Qualifications", path: "/qualifications", icon: <TableChartIcon /> },
        { label: "Certifications", path: "/certifications", icon: <TableChartIcon /> },
      ],
    },
  ];

  const flatMenu = useMemo(() => {
    const all = [];
    menuGroups.forEach((group) => {
      group.items.forEach((item) => {
        all.push({ label: item.label, path: item.path || item.url });
      });
    });
    return all;
  }, [menuGroups]);

  const getPageTitle = () => {
    const currentPath = location.pathname.toLowerCase();
    if (currentPath.startsWith("/profile")) {
      const tab = new URLSearchParams(location.search).get("tab") || "app";
      const map = { app: "Profile.", details: "Details.", settings: "Settings." };
      return map[tab] || "Profile";
    }
    const match = flatMenu.find(
      (item) => item.path && currentPath.startsWith(String(item.path).toLowerCase())
    );
    return match ? match.label : "Admin Portal";
  };

  const handleMenuClick = async (event) => {
    setAnchorEl(event.currentTarget);
    setActiveGroup(null);
    try {
      const { data } = await api.get("/skills", {
        params: { status: "pending" },
        withCredentials: true,
      });
      setPendingSkillsCount(Array.isArray(data) ? data.length : 0);
    } catch {}
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveGroup(null);
  };
  const handleNavigate = (path, external = false) => {
    handleClose();
    if (external) window.open(path, "_blank");
    else navigate(path);
  };
  const activeMenuGroup = menuGroups.find((group) => group.label === activeGroup);
  const handleGroupActivate = (label, event) => {
    setActiveGroup(label);
    if (event?.currentTarget) {
      setActiveGroupTop(event.currentTarget.offsetTop || 0);
    }
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      {/* 🔁 Reverted TOP BAR to the original floating/transparent style */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "rgba(7,17,31,0.82)",
          px: 2,
          py: 1,
          boxShadow: "none",
          backdropFilter: "blur(18px)",
          borderBottom: `1px solid ${BRAND.line}`,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: "64px" }}>
          <Box
            component="img"
            src={BRAND.logo}
            alt="NetDefenders"
            sx={{
              height: { xs: 30, md: 40 },
              width: "auto",
              maxWidth: { xs: 170, md: 230 },
              flexShrink: 0,
            }}
          />
          <Typography
            variant="h3"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: 800,
              color: BRAND.text,
              letterSpacing: "-0.035em",
              userSelect: "none",
              position: "absolute",
              left: 0,
              right: 0,
              pointerEvents: "none",
            }}
          >
            {getPageTitle()}
          </Typography>

          <IconButton edge="end" onClick={handleMenuClick} sx={{ p: 1.5 }}>
            <Badge
              variant="dot"
              color="error"
              invisible={!hasPending}
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{
                "& .MuiBadge-dot": { height: 12, minWidth: 12, borderRadius: "50%" },
                "& .MuiBadge-anchorOriginTopRightCircular": { right: 4, top: 8 },
              }}
            >
              <MenuIcon sx={{ width: 40, height: 40, color: BRAND.accent }} />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ✅ Keep the updated SIDE MENU (popover) styling */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            position: "relative",
            borderRadius: 2,
            mt: 1,
            px: 1,
            py: 1,
            backgroundColor: "#f4f8fb",
            color: BRAND.bg,
            width: { xs: "60vw", sm: "260px" },
            boxShadow: "0px 6px 24px rgba(0,0,0,0.18)",
            overflow: "visible",
            border: "1px solid rgba(0,0,0,0.06)",
          },
          onMouseLeave: isDesktop ? () => setActiveGroup(null) : undefined,
        }}
      >
        <List dense disablePadding sx={{ position: "relative", overflow: "visible" }}>
          <Box sx={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto" }}>
            {(isDesktop || !activeGroup) &&
              menuGroups.map((group, index) => (
                <React.Fragment key={group.label}>
                  {index > 0 && <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />}
                  <ListItem
                    onClick={() => {
                      if (!isDesktop) handleGroupActivate(group.label);
                    }}
                    onMouseEnter={(event) => {
                      if (isDesktop) handleGroupActivate(group.label, event);
                    }}
                    sx={{
                      borderRadius: 1.2,
                      px: 2,
                      py: 1.25,
                      minHeight: 46,
                      cursor: "pointer",
                      "& .MuiListItemText-primary": {
                        fontSize: { xs: "1rem", sm: "0.92rem" },
                        fontWeight: 800,
                        color: BRAND.bg,
                      },
                      "&:hover": { backgroundColor: "rgba(77,225,193,0.12)" },
                    }}
                  >
                    <ListItemIcon sx={{ color: BRAND.accent2, minWidth: 36 }}>
                      {group.icon}
                    </ListItemIcon>
                    <ListItemText primary={group.label} />
                  </ListItem>
                </React.Fragment>
              ))}

            {!isDesktop && activeGroup && activeMenuGroup && (
              <>
                <ListItem
                  onClick={() => setActiveGroup(null)}
                  sx={{
                    borderRadius: 1.2,
                    px: 2,
                    py: 1.25,
                    minHeight: 46,
                    cursor: "pointer",
                    "& .MuiListItemText-primary": {
                      fontSize: { xs: "0.98rem", sm: "0.9rem" },
                      fontWeight: 700,
                      color: BRAND.bg,
                    },
                    "&:hover": { backgroundColor: "rgba(77,225,193,0.12)" },
                  }}
                >
                  <ListItemIcon sx={{ color: BRAND.accent2, minWidth: 36 }}>
                    <ArrowBackIcon />
                  </ListItemIcon>
                  <ListItemText primary="Back" />
                </ListItem>

                <ListSubheader
                  disableSticky
                  sx={{
                    color: BRAND.accent,
                    backgroundColor: "transparent",
                    fontSize: "0.98rem",
                    fontWeight: 800,
                    px: 2,
                    pt: 1.5,
                  }}
                >
                  {activeMenuGroup.label}
                </ListSubheader>

                {activeMenuGroup.items.map((item) => {
                  const isSkills = item.label === "Services Offered";
                  const isExternal = Boolean(item.external);
                  const onClick = () => handleNavigate(item.path || item.url, isExternal);

                  return (
                    <ListItem
                      key={item.label}
                      onClick={onClick}
                      sx={{
                        borderRadius: 1.2,
                        px: 2,
                        py: 1.25,
                        minHeight: 46,
                        cursor: "pointer",
                        "& .MuiListItemText-primary": {
                          fontSize: { xs: "1rem", sm: "0.92rem" },
                          fontWeight: 700,
                          color: BRAND.bg,
                        },
                        "&:hover": { backgroundColor: "rgba(77,225,193,0.12)" },
                      }}
                    >
                      <ListItemIcon sx={{ color: BRAND.accent2, minWidth: 36 }}>
                        {isSkills ? (
                          <Badge
                            variant="dot"
                            color="error"
                            invisible={!hasPending}
                            overlap="circular"
                          >
                            {item.icon}
                          </Badge>
                        ) : (
                          item.icon
                        )}
                      </ListItemIcon>

                      <ListItemText primary={item.label} />
                    </ListItem>
                  );
                })}
              </>
            )}
          </Box>

          {isDesktop && activeMenuGroup && (
            <Box
              sx={{
                position: "absolute",
                right: "100%",
                top: activeGroupTop,
                mt: 0,
                mr: 1,
                width: 260,
                maxHeight: "calc(100vh - 160px)",
                overflowY: "auto",
                backgroundColor: "#f4f8fb",
                color: BRAND.bg,
                borderRadius: 2,
                boxShadow: "0px 6px 24px rgba(0,0,0,0.18)",
                border: "1px solid rgba(0,0,0,0.06)",
                py: 1,
              }}
            >
              <List dense disablePadding>
                <ListSubheader
                  disableSticky
                  sx={{
                    color: BRAND.accent,
                    backgroundColor: "transparent",
                    fontSize: "0.98rem",
                    fontWeight: 800,
                    px: 2,
                    pt: 1.5,
                  }}
                >
                  {activeMenuGroup.label}
                </ListSubheader>
                {activeMenuGroup.items.map((item) => {
                  const isSkills = item.label === "Services Offered";
                  const isExternal = Boolean(item.external);
                  const onClick = () => handleNavigate(item.path || item.url, isExternal);

                  return (
                    <ListItem
                      key={item.label}
                      onClick={onClick}
                      sx={{
                        borderRadius: 1.2,
                        px: 2,
                        py: 1.25,
                        minHeight: 46,
                        cursor: "pointer",
                        "& .MuiListItemText-primary": {
                          fontSize: { xs: "1rem", sm: "0.92rem" },
                          fontWeight: 700,
                          color: BRAND.bg,
                        },
                        "&:hover": { backgroundColor: "rgba(77,225,193,0.12)" },
                      }}
                    >
                      <ListItemIcon sx={{ color: BRAND.accent2, minWidth: 36 }}>
                        {isSkills ? (
                          <Badge
                            variant="dot"
                            color="error"
                            invisible={!hasPending}
                            overlap="circular"
                          >
                            {item.icon}
                          </Badge>
                        ) : (
                          item.icon
                        )}
                      </ListItemIcon>

                      <ListItemText primary={item.label} />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />

          <ListItem
            onClick={handleLogout}
            sx={{
              borderRadius: 1.2,
              px: 2,
              py: 1.25,
              minHeight: 46,
              cursor: "pointer",
              "& .MuiListItemText-primary": {
                fontSize: { xs: "1rem", sm: "0.92rem" },
                fontWeight: 700,
                color: "#c62828",
              },
              "&:hover": { backgroundColor: "rgba(198,40,40,0.08)" },
            }}
          >
            <ListItemIcon sx={{ color: "#c62828", minWidth: 36 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default AdminTopNavBar;

AdminTopNavBar.propTypes = {
  forcedRole: PropTypes.string,
};

AdminTopNavBar.defaultProps = {
  forcedRole: undefined,
};
